<script lang="ts">
  import ContactOrderPlanningSurface, {
    type ContactOrderPlanningDraft,
  } from "../../../../../../../_shared/_modules/contact-order-planning-surface"
  import { domainContactOrderPlanningOperations } from "../../../../../operations"
  import type {
    ContactOrderPlanningSurfacePayloadBuilderInput,
    ContactOrderPlanningSurfaceResult,
    ContactOrderPlanningSubmissionResult,
  } from "../../../../../../../_shared/_logic/contact-orders-contract"
  import { buildContactOrderBatchPayloads } from "../../../../../../../_shared/_logic/contact-orders-payload"
  import type {
    CancelReplanInit,
    CancelReplanMode,
    CancelReplanWorkflowEvent,
    PlannedRow,
    PlanReplacementsStepOutput,
  } from "../../../../_logic/cancel-replan-contract"
  import {
    buildActiveContactOrderByWorkOrderId,
    buildWorkOrderScheduleBatchRows,
  } from "../../../../_logic/rows"

  let {
    planningOutput,
    planningDraft,
    init,
    mode = "rescheduleAppointments",
    onBack,
    output,
    readOnly = false,
    onEvent,
    onError,
  }: {
    planningOutput?: PlanReplacementsStepOutput
    planningDraft?: ContactOrderPlanningDraft
    init?: CancelReplanInit
    mode?: CancelReplanMode
    onBack?: () => void
    output?: { result: ContactOrderPlanningSurfaceResult["result"] }
    readOnly?: boolean
    onEvent: (event: CancelReplanWorkflowEvent) => void | Promise<void>
    onError: (message: string | null) => void
  } = $props()

  const propertyById = $derived(
    new Map((init?.properties ?? []).map((property) => [property.id, property])),
  )
  const workOrderRowById = $derived(new Map((init?.sourceRows ?? []).map((row) => [row.id, row])))
  const eventByWorkOrderId = $derived(
    Object.fromEntries(
      (init?.events ?? [])
        .filter((event): event is ScheduleEvent & { work_order_id: string } =>
          Boolean(event.work_order_id),
        )
        .map((event) => [event.work_order_id, event]),
    ),
  )
  const activeContactOrderByWorkOrderId = $derived(
    buildActiveContactOrderByWorkOrderId(init?.contactOrders ?? []),
  )
  const directRows = $derived<PlannedRow[]>(
    init
      ? buildWorkOrderScheduleBatchRows({
          workOrders: init.workOrders,
          eventByWorkOrderId,
          workOrderRowById,
          propertyById,
          activeContactOrderByWorkOrderId,
        })
          .map((row): PlannedRow | null =>
            row.event
              ? { id: row.id, row, preview: null, event: row.event, previousEvent: row.event }
              : null,
          )
          .filter((row): row is PlannedRow => Boolean(row))
      : [],
  )
  const scheduledRows = $derived(
    (planningOutput?.plannedRows ?? directRows).filter(
      (row): row is PlannedRow & { event: ScheduleEvent } => Boolean(row.event),
    ),
  )
  const scheduledEvents = $derived(scheduledRows.map((row) => row.event))
  const workOrders = $derived(
    scheduledRows
      .map((row) => row.row.workOrder)
      .filter((order): order is WorkOrder => Boolean(order)),
  )
  const contactOrders = $derived(output?.result.created ?? [])
  const employeeOptions = $derived(
    (init?.employees ?? [])
      .map((entry) => ({ label: entry.label, value: String(entry.value ?? entry.id ?? "").trim() }))
      .filter((entry) => entry.value),
  )
  const caseIdByOrderId = $derived(
    Object.fromEntries(workOrders.map((order) => [order.id, order.case_id ?? null])),
  )
  const isRescheduleMode = $derived(mode === "rescheduleAppointments")

  const buildReplanContactOrderPayloads = (
    input: ContactOrderPlanningSurfacePayloadBuilderInput,
  ) => {
    const rowByEventId = new Map(scheduledRows.map((row) => [row.event.id, row]))

    return buildContactOrderBatchPayloads(input).map((payload) => {
      const row = payload.schedule_event_id ? rowByEventId.get(payload.schedule_event_id) : null
      const previousEvent = row?.previousEvent ?? row?.row.event ?? null
      const isReschedule = mode === "rescheduleAppointments"

      return {
        ...payload,
        case_id: row?.row.workOrder?.case_id ?? payload.case_id ?? null,
        reason: isReschedule ? "RESCHEDULE" : "INITIAL",
        ...(isReschedule
          ? {
              previous_schedule_event_id: previousEvent?.id,
              canceled_schedule_event_id: previousEvent?.id,
              previous_appointment_start_at: previousEvent?.start_at,
              previous_appointment_end_at: previousEvent?.end_at,
              previous_schedule_event_status: previousEvent?.status,
            }
          : {}),
        replace_existing: true,
      }
    })
  }

  const handleSubmissionResult = async (submission: ContactOrderPlanningSubmissionResult) => {
    if (submission.kind !== "committed" || submission.status !== "completed") return

    await onEvent({
      type: "announce.completed",
      output: {
        result: submission.result.result,
      },
    })
  }
</script>

{#if init}
  <ContactOrderPlanningSurface
    {planningDraft}
    organizationId={init.organizationId}
    events={scheduledEvents}
    {contactOrders}
    {workOrders}
    properties={init.properties}
    customers={init.customers ?? []}
    contactPoints={init.contactPoints ?? []}
    {employeeOptions}
    {caseIdByOrderId}
    operations={domainContactOrderPlanningOperations}
    {readOnly}
    showContinueButton={false}
    createButtonLabel={isRescheduleMode ? "Kunden informieren" : "Ankündigungspläne anlegen"}
    buildPayloads={buildReplanContactOrderPayloads}
    setError={onError}
    {onBack}
    onSubmissionResult={handleSubmissionResult}
  />
{/if}
